import config from '@platform/shared/config';
import type { IFileTransaction } from '@platform/store/file-transactions';
import { useFileTransactionsStore } from '@platform/store/file-transactions';
import type { AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import Axios from 'axios';
import { v4 as guid } from 'uuid';
import { serviceOptions } from './index';
import { saveAs } from 'file-saver';
import TabsStore from '@platform/store/tabs';

type AxiosExtendedConfig = AxiosRequestConfig & { trId?: string; filename?: string };

// Создаем экземпляр Axios с базовой конфигурацией
const axios = Axios.create({
    baseURL: config.apiURLFileStoreV2,
    headers: {
        'Service-ID': config.serviceId,
        'Filestore-Api-Key': config.apiKeyFileStore,
    },
});
// const FileTransactionsStore = useFileTransactionsStore(); // todo need to help

// Регистрируем хуки аутентификации для Axios
import('@platform/shared/rpc/node-auth')
    .then(({ registerAxiosForAuthHooks }) => {
        return registerAxiosForAuthHooks(axios);
    })
    .catch(console.error);

// Получаем экземпляр Pinia store
// Добавляем Интерцептор для обработки запросов
axios.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    let direction: IFileTransaction['direction'];
    if (config.url?.endsWith('/save')) direction = 'upload';
    else if (config.responseType === 'blob') {
        direction = 'download';
    } else {
        return config;
    }
    const id = guid();
    let name = '';
    if (config.data && config.data instanceof FormData) {
        name = (config.data.get('name') as string | null) ?? '';
    } else if ('filename' in config && typeof config.filename === 'string') {
        name = config.filename;
    }
    const FileTransactionsStore = useFileTransactionsStore();
    const existingOnUploadProgress = config.onUploadProgress;
    // Обновляем прогресс загрузки
    config.onUploadProgress = (event) => {
        try {
            existingOnUploadProgress?.(event);
        } catch (e) {
            console.error('Error in onUploadProgress', e);
        }
        if (direction !== 'upload') return;
        FileTransactionsStore.update({ id, total: event.total, progress: event.loaded });
    };
    // Обновляем прогресс скачивания
    config.onDownloadProgress = (event) => {
        if (direction !== 'download') return;
        FileTransactionsStore.update({ id, total: event.total, progress: event.loaded });
    };
    const cancelToken = Axios.CancelToken.source();
    config.cancelToken = cancelToken.token;
    const systemSectionLabel = TabsStore.getTabs()?.find((tab) => tab.key === TabsStore.getSelectedTab())?.label;
    // Добавляем новую транзакцию в FileTransactionsStore
    FileTransactionsStore.push({
        date: +new Date(),
        id,
        direction,
        name,
        isFinished: false,
        total: 0,
        progress: 0,
        cancel: () => cancelToken.cancel('Запрос был отменен пользователем'),
        systemSectionLabel,
    });
    (config as any).trId = id;
    return config;
});

// Добавляем интерсептор для обработки ответов
axios.interceptors.response.use(
    (response) => {
        const trId = (response.config as AxiosExtendedConfig).trId;
        if (trId) {
            let download: (() => void) | undefined;
            if (response.config.responseType === 'blob') {
                download = () => saveAs(response.data, (response.config as AxiosExtendedConfig).filename);
            }
            const FileTransactionsStore = useFileTransactionsStore();
            // Обновляем статус транзакции в FileTransactionsStore
            FileTransactionsStore.update({ id: trId, isFinished: true, download });
        }
        return response;
    },
    (error) => {
        if (error?.config?.trId) {
            const FileTransactionsStore = useFileTransactionsStore();
            // Обновляем статус транзакции в случае ошибки
            FileTransactionsStore.update({ id: error.config.trId, isFinished: true, error: error });
        }
    }
);

// Устанавливаем экземпляр axios в serviceOptions
serviceOptions.axios = axios;
